import { useState, useEffect } from 'react';
import { analyticsAPI } from '../lib/api';
import { Loader2, PhoneCall, PhoneIncoming, Clock, TrendingUp, Users, BarChart3 } from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-white/10 rounded-lg p-3 shadow-xl text-sm">
      <p className="text-slate-400 mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color }} className="font-medium">
          {entry.name}: {entry.value}
        </p>
      ))}
    </div>
  );
};

const COLORS = ['#F59E0B', '#06B6D4', '#10B981', '#F59E0B', '#EF4444'];

export default function AnalyticsPage() {
  const [overview, setOverview] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([analyticsAPI.overview(), analyticsAPI.charts()])
      .then(([ovRes, chRes]) => { setOverview(ovRes.data); setCharts(chRes.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-amber-500 animate-spin" /></div>;
  }

  const pieData = [
    { name: 'Completed', value: overview?.answered_calls || 0 },
    { name: 'Missed', value: Math.max((overview?.total_calls || 0) - (overview?.answered_calls || 0), 0) },
  ];

  return (
    <div className="space-y-8 max-w-7xl" data-testid="analytics-page">
      <div>
        <h1 className="text-3xl font-bold font-['Outfit'] text-white">Analytics</h1>
        <p className="text-slate-400 mt-1">Performance insights and metrics</p>
      </div>

      {/* Top Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: PhoneCall, label: 'Total Calls', value: overview?.total_calls || 0, color: 'bg-amber-600/20 text-amber-400' },
          { icon: PhoneIncoming, label: 'Answered', value: overview?.answered_calls || 0, color: 'bg-emerald-600/20 text-emerald-400' },
          { icon: Clock, label: 'Avg Duration', value: `${Math.floor((overview?.avg_duration || 0) / 60)}m`, color: 'bg-cyan-600/20 text-cyan-400' },
          { icon: TrendingUp, label: 'Conversion Rate', value: `${overview?.conversion_rate || 0}%`, color: 'bg-amber-600/20 text-amber-400' },
        ].map((m, i) => (
          <div key={i} className="glass-card rounded-xl p-5 animate-fade-in" style={{ animationDelay: `${i * 80}ms` }}
            data-testid={`analytics-metric-${i}`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${m.color}`}>
              <m.icon className="w-5 h-5" />
            </div>
            <div className="text-2xl font-bold font-['Outfit'] text-white">{m.value}</div>
            <div className="text-sm text-slate-500 mt-1">{m.label}</div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Call Volume */}
        <div className="lg:col-span-8 glass-card rounded-xl p-6" data-testid="analytics-volume-chart">
          <h3 className="text-lg font-semibold font-['Outfit'] text-white mb-4">Call Volume Trend</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={charts?.daily || []}>
                <defs>
                  <linearGradient id="gCalls" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gAnswered" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06B6D4" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#06B6D4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" stroke="#475569" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#475569" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="calls" stroke="#F59E0B" fill="url(#gCalls)" strokeWidth={2} name="Total Calls" />
                <Area type="monotone" dataKey="answered" stroke="#06B6D4" fill="url(#gAnswered)" strokeWidth={2} name="Answered" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pie Chart */}
        <div className="lg:col-span-4 glass-card rounded-xl p-6" data-testid="analytics-pie-chart">
          <h3 className="text-lg font-semibold font-['Outfit'] text-white mb-4">Call Outcomes</h3>
          <div className="h-72 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={4} dataKey="value">
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-2">
            {pieData.map((d, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i] }} />
                <span className="text-slate-400">{d.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conversions */}
        <div className="glass-card rounded-xl p-6" data-testid="analytics-conversions-chart">
          <h3 className="text-lg font-semibold font-['Outfit'] text-white mb-4">Daily Conversions</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts?.daily || []}>
                <XAxis dataKey="date" stroke="#475569" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#475569" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="conversions" fill="#10B981" radius={[4, 4, 0, 0]} name="Conversions" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Duration */}
        <div className="glass-card rounded-xl p-6" data-testid="analytics-duration-chart">
          <h3 className="text-lg font-semibold font-['Outfit'] text-white mb-4">Avg Call Duration (seconds)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={charts?.daily || []}>
                <XAxis dataKey="date" stroke="#475569" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#475569" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="duration" stroke="#F59E0B" strokeWidth={2} dot={{ fill: '#F59E0B', r: 3 }} name="Duration" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Usage */}
      <div className="glass-card rounded-xl p-6" data-testid="analytics-usage">
        <h3 className="text-lg font-semibold font-['Outfit'] text-white mb-4">Platform Usage</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <div className="text-sm text-slate-500 mb-1">AI Agents</div>
            <div className="text-2xl font-bold font-['Outfit'] text-white">{overview?.total_agents || 0}</div>
          </div>
          <div>
            <div className="text-sm text-slate-500 mb-1">Campaigns</div>
            <div className="text-2xl font-bold font-['Outfit'] text-white">{overview?.total_campaigns || 0}</div>
          </div>
          <div>
            <div className="text-sm text-slate-500 mb-1">Minutes Used</div>
            <div className="text-2xl font-bold font-['Outfit'] text-white">{overview?.usage_minutes || 0}</div>
          </div>
          <div>
            <div className="text-sm text-slate-500 mb-1">KB Documents</div>
            <div className="text-2xl font-bold font-['Outfit'] text-white">{overview?.total_kb_docs || 0}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
